import { useCallback, useEffect, useRef, useState } from 'react';

import { VONAGE_APP_ID } from '../../../environment';
import {
  computeCallOutcome,
  createCallMetrics,
  isCallSettled,
} from '../callOutcome';
import { reportCallOutcome } from '../reportCallOutcome';
import { reportVonageError } from '../videoErrorReporting';

// Shared by the practitioner and client video screens. The only role-specific
// piece is getDivId (which participant renders into which div); `connected`
// is only consumed by the client screen for its toast.
export function useVonageSession({
  appointment,
  user,
  token,
  role,
  getDivId,
  couplesPartnerDivId,
  phoneCallMediaDivId,
  onPhoneCallConnected,
  onPhoneCallDisconnected,
  initialSettings,
  onJoined,
  captionsEnabled = false,
  onCaptionReceived,
  onCaptionStreamDestroyed,
  onCaptionsInterrupted,
}) {
  const onPhoneCallConnectedRef = useRef(onPhoneCallConnected);
  const onPhoneCallDisconnectedRef = useRef(onPhoneCallDisconnected);
  const onJoinedRef = useRef(onJoined);
  const onCaptionReceivedRef = useRef(onCaptionReceived);
  const onCaptionStreamDestroyedRef = useRef(onCaptionStreamDestroyed);
  const onCaptionsInterruptedRef = useRef(onCaptionsInterrupted);
  const captionsEnabledRef = useRef(captionsEnabled);
  const subscribersByStreamIdRef = useRef(new Map());
  onPhoneCallConnectedRef.current = onPhoneCallConnected;
  onPhoneCallDisconnectedRef.current = onPhoneCallDisconnected;
  onJoinedRef.current = onJoined;
  onCaptionReceivedRef.current = onCaptionReceived;
  onCaptionStreamDestroyedRef.current = onCaptionStreamDestroyed;
  onCaptionsInterruptedRef.current = onCaptionsInterrupted;
  captionsEnabledRef.current = captionsEnabled;

  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [publisherObj, setPublisherObj] = useState(null);
  const [hasVideoSource, setHasVideoSource] = useState(false);
  const [participantPresent, setParticipantPresent] = useState(false);
  const [partnerPresent, setPartnerPresent] = useState(false);

  // Call-outcome instrumentation (POD2-2117). OT callbacks are registered once,
  // so signals accumulate in a ref (useState would go stale in the closures).
  const metricsRef = useRef(createCallMetrics());
  const outcomeReportedRef = useRef(false);

  const reportOutcome = useCallback(() => {
    // Exactly one outcome per participant; only report a session we attempted.
    if (
      outcomeReportedRef.current ||
      metricsRef.current.joinAttemptedAt == null ||
      !token
    )
      return;
    outcomeReportedRef.current = true;

    const { outcome, reason, timings, signals } = computeCallOutcome(
      metricsRef.current,
    );
    reportCallOutcome({
      appointmentId: appointment?.id,
      vonageSessionId: appointment?.vonage_session_id,
      sessionToken: token,
      outcome,
      reason,
      hadReconnect: metricsRef.current.hadReconnect,
      timings,
      remoteConnectionSeen: signals.remoteConnectionSeen,
      remoteStreamSeen: signals.remoteStreamSeen,
      publishSucceeded: signals.publishSucceeded,
      role: role ?? null,
    });
  }, [appointment?.id, appointment?.vonage_session_id, token]);

  const setSubscriberCaptions = useCallback(
    async (subscriber, enabled) => {
      try {
        if (subscriber.isSubscribedToCaptions() === enabled) return;
        await subscriber.subscribeToCaptions(enabled);
      } catch (err) {
        reportVonageError(err, 'captions-subscribe', {
          appointmentId: appointment?.id,
          vonageSessionId: appointment?.vonage_session_id,
        });
      }
    },
    [appointment?.id, appointment?.vonage_session_id],
  );

  useEffect(() => {
    subscribersByStreamIdRef.current.forEach(subscriber => {
      setSubscriberCaptions(subscriber, captionsEnabled);
    });
  }, [captionsEnabled, setSubscriberCaptions]);
  // The flush effect below must not re-subscribe when this callback's identity
  // changes. A token change used to run its cleanup, file an outcome for the
  // previous session, and latch outcomeReportedRef so the current session was
  // never recorded (POD2-2431).
  const reportOutcomeRef = useRef(reportOutcome);
  reportOutcomeRef.current = reportOutcome;

  // Keyed on [token] only: the session is (re)built once per token, matching
  // the original hooks. appointment and getDivId are stable for a session, so
  // reading them from the closure here is intentional, not a missing dep.
  useEffect(() => {
    // Redux keeps the previous appointment's token across the unmount between
    // calls, so this effect's first run can connect with that stale token and
    // fail auth. Drop anything it latched before building the session for the
    // token we have now; the flush effect only reports on unmount, so this
    // does not file an outcome for the token we just left.
    metricsRef.current = createCallMetrics();
    outcomeReportedRef.current = false;
    setError(null);

    if (!token || !user?.id) return;

    // Set before any callback that reads it. A stale token's connect callback
    // can resolve after this effect has already been cleaned up.
    let disposed = false;

    const reportError = (err, area) => {
      reportVonageError(err, area, {
        appointmentId: appointment?.id,
        vonageSessionId: appointment?.vonage_session_id,
      });
    };

    // Records the first fatal OpenTok error code for outcome reason mapping.
    // Only called from fatal areas (sdk/webrtc/session/connect/publish); the
    // transient subscribe path deliberately does not mark a session failed.
    const recordFatal = err => {
      if (disposed) return;
      if (
        metricsRef.current.fatalErrorCode == null &&
        (err?.name || err?.code)
      ) {
        metricsRef.current.fatalErrorCode = err?.name ?? String(err?.code);
      }
    };

    const { OT } = window;
    // Mark the join attempt up-front so pre-join fatals (SDK missing, WebRTC
    // unsupported) below still emit a `failed` outcome — the report guard keys
    // off joinAttemptedAt, and those early returns happen before session.connect.
    if (metricsRef.current.joinAttemptedAt == null)
      metricsRef.current.joinAttemptedAt = performance.now();
    if (!OT) {
      const err = new Error('Vonage SDK not loaded');
      reportError(err, 'sdk-not-loaded');
      recordFatal(err);
      setError(err);
      return;
    }

    if (OT.checkSystemRequirements() !== 1) {
      const err = new Error('WebRTC not supported');
      reportError(err, 'webrtc-unsupported');
      recordFatal(err);
      setError(err);
      return;
    }

    const session = OT.initSession(
      VONAGE_APP_ID,
      appointment.vonage_session_id,
    );

    // Tracks stream ids already handled for this session so a stream signalled
    // more than once is only subscribed/added once. Declared inside the effect
    // so it is read synchronously by the handlers (the old guard closed over the
    // first-render remoteStreams and never dedup'd) and reset per session/token.
    const handledStreamIds = new Set();
    const subscribersByStreamId = subscribersByStreamIdRef.current;

    const registerSubscriber = (stream, subscriber) => {
      if (!subscriber) return;

      subscribersByStreamId.set(stream.id, subscriber);
      subscriber.on('captionReceived', evt => {
        onCaptionReceivedRef.current?.({
          caption: evt.caption,
          streamId: evt.streamId ?? stream.id,
          isFinal: Boolean(evt.isFinal),
          participantId: stream.name,
          connectionId: stream.connection?.id,
        });
      });

      if (captionsEnabledRef.current) {
        setSubscriberCaptions(subscriber, true);
      }
    };

    session.on({
      sessionConnected: () => {
        if (metricsRef.current.joinedAt == null)
          metricsRef.current.joinedAt = performance.now();
        setConnected(true);
      },
      sessionDisconnected: () => {
        setConnected(false);
      },
      sessionReconnecting: () => {
        metricsRef.current.hadReconnect = true;
        onCaptionsInterruptedRef.current?.();
      },
      sessionReconnected: () => {},
      error: err => {
        if (disposed) return;
        reportError(err, 'session-error');
        recordFatal(err);
        setError(err);
      },
      connectionCreated: evt => {
        // session.connection may not be populated yet, so a named streamCreated
        // below latches this too.
        if (
          evt?.connection?.connectionId &&
          evt.connection.connectionId !== session.connection?.connectionId
        ) {
          metricsRef.current.remoteConnectionSeen = true;
        }
      },
      connectionDestroyed: () => {},
      streamCreated: async evt => {
        if (handledStreamIds.has(evt.stream.id)) return;
        handledStreamIds.add(evt.stream.id);

        if (!evt.stream.name) {
          if (evt.stream.connection?.id) {
            const shouldSubscribe = await onPhoneCallConnectedRef.current?.(
              evt.stream.connection.id,
            );
            if (shouldSubscribe) {
              const subscriber = session.subscribe(
                evt.stream,
                phoneCallMediaDivId,
                // 'append' keeps the hidden container in the DOM. The default
                // 'replace' swaps it out, so a later phone call would find no
                // container and OpenTok would render its video in <body>.
                { insertMode: 'append', subscribeToCaptions: false },
              );
              registerSubscriber(evt.stream, subscriber);
            }
          }
          return;
        }

        // A named stream is a real remote peer, so latch both signals here -
        // independent of whether our own subscribe below succeeds.
        metricsRef.current.remoteConnectionSeen = true;
        metricsRef.current.remoteStreamSeen = true;

        const divId = getDivId(evt.stream.name);

        const subscriber = session.subscribe(
          evt.stream,
          divId,
          {
            insertMode: 'append',
            width: '100%',
            height: '100%',
            style: {
              buttonDisplayMode: 'off',
              nameDisplayMode: 'off',
            },
            subscribeToCaptions: false,
          },
          err => {
            if (err) {
              reportError(err, 'subscribe');
              setError(err);
            } else {
              // First named remote stream subscribed = the remote leg of two-way audio.
              if (metricsRef.current.firstRemoteAt == null) {
                metricsRef.current.firstRemoteAt = performance.now();
              }
              if (divId === couplesPartnerDivId) {
                setPartnerPresent(true);
              } else {
                setParticipantPresent(true);
              }
            }
          },
        );
        registerSubscriber(evt.stream, subscriber);
      },
      streamDestroyed: evt => {
        handledStreamIds.delete(evt.stream.id);
        subscribersByStreamId.delete(evt.stream.id);
        onCaptionStreamDestroyedRef.current?.(evt.stream.id);

        if (!evt.stream.name) {
          if (evt.stream.connection?.id) {
            onPhoneCallDisconnectedRef.current?.(evt.stream.connection.id);
          }
          return;
        }

        const divId = getDivId(evt.stream.name);

        if (divId === couplesPartnerDivId) {
          setPartnerPresent(false);
        } else {
          setParticipantPresent(false);
        }
      },
    });

    let activePublisher;
    let sessionConnected = false;
    let retriedWithoutPinnedSources = false;

    const legacyOptions = {
      showControls: false,
      name: user.id,
      insertMode: 'append',
      width: '100%',
      height: '100%',
      publishCaptions: true,
    };

    const buildPublisherOptions = pinSources => {
      if (!initialSettings) return legacyOptions;
      const options = {
        ...legacyOptions,
        publishAudio: initialSettings.isMicrophoneEnabled,
        publishVideo: Boolean(
          initialSettings.cameraDeviceId && initialSettings.isCameraEnabled,
        ),
      };
      if (pinSources && initialSettings.microphoneDeviceId)
        options.audioSource = initialSettings.microphoneDeviceId;
      if (!initialSettings.cameraDeviceId) options.videoSource = false;
      else if (pinSources) options.videoSource = initialSettings.cameraDeviceId;
      return options;
    };

    const publish = publisher => {
      session.publish(publisher, errPub => {
        if (errPub && !disposed && publisher === activePublisher) {
          reportError(errPub, 'publish');
          recordFatal(errPub);
          setError(errPub);
        } else if (
          !errPub &&
          !disposed &&
          publisher === activePublisher &&
          metricsRef.current.publishedAt == null
        ) {
          metricsRef.current.publishedAt = performance.now();
        }
      });
    };

    const createPublisher = pinSources => {
      const publisher = OT.initPublisher(
        'local-media',
        buildPublisherOptions(pinSources),
        err => {
          if (!err || disposed) return;
          const errorName = String(err.name || '');
          const staleSource =
            errorName === 'OT_HARDWARE_UNAVAILABLE' ||
            errorName === 'OT_NO_DEVICES_FOUND' ||
            errorName === 'OT_CONSTRAINTS_NOT_SATISFIED' ||
            (Number(err.code) === 1500 &&
              errorName !== 'OT_USER_MEDIA_ACCESS_DENIED');
          const canRetry =
            initialSettings &&
            pinSources &&
            !retriedWithoutPinnedSources &&
            Boolean(
              initialSettings.microphoneDeviceId ||
                initialSettings.cameraDeviceId,
            ) &&
            staleSource;
          if (canRetry) {
            retriedWithoutPinnedSources = true;
            Promise.resolve().then(() => {
              if (disposed) return;
              try {
                activePublisher?.destroy();
              } catch (destroyError) {
                reportError(destroyError, 'cleanup');
              }
              activePublisher = createPublisher(false);
              if (sessionConnected) publish(activePublisher);
            });
            return;
          }
          reportError(err, 'init-publisher');
          recordFatal(err);
          setError(err);
        },
      );
      publisher.on('streamCreated', () => {});
      publisher.on('streamDestroyed', () => {});
      setPublisherObj(publisher);
      setHasVideoSource(
        initialSettings ? Boolean(initialSettings.cameraDeviceId) : true,
      );
      return publisher;
    };

    if (
      initialSettings?.speakerDeviceId &&
      initialSettings.speakerDeviceId !== 'default' &&
      typeof OT.setAudioOutputDevice === 'function'
    ) {
      OT.setAudioOutputDevice(initialSettings.speakerDeviceId).catch(() =>
        OT.setAudioOutputDevice('default').catch(() => {}),
      );
    }

    activePublisher = createPublisher(true);

    session.connect(token, err => {
      if (disposed) return;
      if (err) {
        reportError(err, 'connect');
        recordFatal(err);
        setError(err);
      } else {
        sessionConnected = true;
        onJoinedRef.current?.();
        publish(activePublisher);
      }
    });

    return () => {
      disposed = true;
      subscribersByStreamId.clear();
      setPublisherObj(null);
      setHasVideoSource(false);
      try {
        if (activePublisher) {
          activePublisher.destroy();
        }
        if (session) {
          session.off();
          session.disconnect();
        }
      } catch (e) {
        reportError(e, 'cleanup');
      }
    };
  }, [token]);

  // Flush exactly one call-outcome beacon when the participant actually leaves.
  // pagehide covers full page unloads; visibilitychange:hidden covers
  // tab-close/background on mobile; the cleanup covers in-app navigation away
  // from the call. reportOutcome is idempotent (outcomeReportedRef), so at most
  // one beacon is sent.
  //
  // Empty deps on purpose: this must run on mount/unmount only. Re-running it
  // when the token changes files a false failure against the previous
  // appointment and suppresses the current session's real result.
  //
  // pagehide and unmount are genuine page-away signals and finalize the outcome
  // unconditionally. visibilitychange:hidden is reversible (a user may
  // background the tab mid-connect and return), so it only finalizes once the
  // call has settled — otherwise a call that later succeeds would be reported
  // as failed.
  useEffect(() => {
    const onPageHide = () => reportOutcomeRef.current();
    const onVisibilityChange = () => {
      if (
        document.visibilityState === 'hidden' &&
        isCallSettled(metricsRef.current)
      )
        reportOutcomeRef.current();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      reportOutcomeRef.current();
    };
  }, []);

  const setCameraEnabled = enabled => {
    if (!publisherObj) return;
    publisherObj.publishVideo(enabled);
  };

  const setMicEnabled = enabled => {
    if (!publisherObj) return;
    publisherObj.publishAudio(enabled);
  };

  return {
    error,
    connected,
    participantPresent,
    partnerPresent,
    publisher: publisherObj,
    hasVideoSource,
    setCameraEnabled,
    setMicEnabled,
  };
}
